// WebSocket reconnect / heartbeat timing.
//
// Heartbeat: ping every 15s; if no pong arrives within 60s, force-close and
// reconnect. The window was 30s originally; bumped to 60s because slow
// real-world links (high-latency mobile, weak coffee-shop wifi) and
// DevTools throttle modes ("Slow 4G", etc.) could push a single ping-pong
// roundtrip past 30s. At that point the dead-check fires, drops the live
// socket, and reconnect cycles forever — every reconnect re-broadcasts
// game_state which flickers downstream "Saved" indicators on a loop. 60s
// is still plenty fast to catch a genuinely dead TCP connection (browsers
// don't notice the OS-level death for ~2 minutes otherwise).
export const WS_HEARTBEAT_INTERVAL_MS = 15_000;
export const WS_HEARTBEAT_TIMEOUT_MS  = 60_000;
export const WS_RECONNECT_BASE_MS     = 250;
export const WS_RECONNECT_MAX_MS      = 5_000;

// Manager card effect types that auto-resolve the at-bat (no pitch roll needed)
export const AUTO_AB_EFFECT_TYPES = new Set(["auto_bunt_single", "bogo_walk", "plunk_them"]);

// Card condition qualifying thresholds — must stay in sync with backend/constants.py
// QUALIFYING_GP_FIELDER / QUALIFYING_IP_STARTER / QUALIFYING_SV_RELIEVER.
export const QUALIFYING_GP_FIELDER   = 100;  // min games_played for a fielder season to qualify
export const QUALIFYING_IP_STARTER   = 100;  // min innings_pitched for a starter season to qualify
export const QUALIFYING_SV_RELIEVER  = 25;   // min saves for a reliever season to qualify

// Game phase names
export const PHASE = Object.freeze({
  PITCH:    "pitch",
  FIELDING: "fielding",
  BASES:    "bases",
});

// ─── Innings select options ───────────────────────────────────────────────────
// Shared by every lobby surface (PvPSetup.OnlineSetup, including the
// deck-builder draft mode that runs inside it).
export const INNINGS_OPTIONS = [
  { value: "1", label: "1 — Sandlot" },
  { value: "2", label: "2 — Tee Ball" },
  { value: "3", label: "3 — Little League" },
  { value: "4", label: "4 — Spring Training" },
  { value: "5", label: "5 — Quick Play" },
  { value: "6", label: "6 — Doubleheader Half" },
  { value: "7", label: "7 — Tournament" },
  { value: "8", label: "8 — Almost There" },
  { value: "9", label: "9 — Full Game" },
];

// ─── Base game-creation defaults ──────────────────────────────────────────────
// Single source of truth for every configurable game setting. Each setup page
// spreads this and overrides innings_total (and any page-specific fields) as
// needed. Timer keys default to null (= use server default).
export const BASE_GAME_DEFAULTS = {
  innings_total: 9,
  ballpark: "",
  excluded_rarities: ["junk_wax"],
  chip_max: 4,
  pitch_mod: 2,
  hand_size: 5,
  require_chip_match_for_hr: true,
  depleting_chips: false,
  chip_arsenal: false,
  chip_arsenal_infinite: 1,
  chip_arsenal_modifiers: [1, 1, 1, 1],
  chip_arsenal_fb_count: 8,
  chip_arsenal_ch_count: 5,
  chip_arsenal_cb_count: 3,
  chip_arsenal_sl_count: 2,
  no_timers: false,
  continuous_draw: false,
  heat_map: false,
  heat_map_overlap_bonus: 1,
  heat_map_chip_bonus: 1,
  heat_map_overhang_threshold: 20,
  heat_map_zone_width_pct: 100,
  same_raw_tie_is_ball: true,
  modifier_tie_is_ball: true,
  any_raw_tie_is_ball: false,
  called_ball_tie_is_strike: false,
  bluff_values: true,
  unlimited_stacking: true,
  // Player draft (Phase 1: toggle + per-position pick counts + draft order).
  player_draft_enabled: false,
  player_draft_position_counts: { P: 3, C: 1, "1B": 1, "2B": 1, "3B": 1, SS: 1, LF: 1, CF: 1, RF: 1, DH: 1, Flex: 1 },
  draft_order: ["deck_builder", "player"],
  pitch_clock_secs: null,
  pitch_pitcher_secs: null,
  pitch_batter_secs: null,
  fielding_clock_secs: null,
  steal_clock_secs: null,
  hand_refresh_secs: null,
  pickoff_select_secs: null,
  card_modal_secs: null,
  swap_spit_secs: null,
  draft_pick_secs: null,
  player_draft_pick_secs: null,
};

// ─── Heat Map pitch mode ───────────────────────────────────────────────────────
// Change these values to resize the strike zone or chip circles.
// IMPORTANT: CHIP_RADIUS, ZONE_WIDTH, and ZONE_HEIGHT are mirrored in
// backend/server.py as _HM_CHIP_RADIUS / _HM_ZONE_WIDTH / _HM_ZONE_HEIGHT.
// Update those three constants too — the server uses them for the overlap check.
export const HEAT_MAP_CONFIG = Object.freeze({
  // Strike zone rectangle — fills the main game board area during pitch phase
  ZONE_WIDTH:  280,   // px — mirror in server.py: _HM_ZONE_WIDTH
  ZONE_HEIGHT: 360,   // px — mirror in server.py: _HM_ZONE_HEIGHT

  // Radius of the placed chip circle inside the zone and of the ghost chip on drag.
  CHIP_RADIUS: 68,    // px — mirror in server.py: _HM_CHIP_RADIUS

  // Heat map blob: a small circle drawn at each pitch location before blur.
  // Keep this well below CHIP_RADIUS so blobs don't merge into one smudge.
  BLOB_CIRCLE_RADIUS: 3,   // px — drawn circle radius before blur

  // Canvas 2D blur filter radius (px) applied per blob.
  // Higher = softer and wider spread; combined with screen blending = colourful haze.
  BLOB_BLUR_PX: 10,

  // Opacity of each heat map blob (0.0 – 1.0). Lower = more transparent/subtle.
  BLOB_OPACITY: 0.1,

  // Canvas bleed: canvas extends this many px beyond the zone on each side
  // so edge blobs can paint softly outside the zone boundary
  CANVAS_BLEED: 40,   // px

  // Fraction of CHIP_RADIUS the chip center is allowed to exceed the zone boundary
  OVERFLOW: 0.2,

  // Pitch type colors — bright variants that read well on dark slate backgrounds
  // Index 0=FB, 1=CH, 2=CB, 3=SL
  CHIP_COLORS: ["#f87171", "#60a5fa", "#4ade80", "#fbbf24"],
  //             FB-red    CH-blue    CB-green   SL-amber
});
